import { Col, NumberInput, Row, Select } from '@ksyos/design-system';
import { clamp } from 'lodash';
import { type JSX, memo, useCallback } from 'react';
import type { Quantity } from '@common/validation/quantity';
import { getValidationErrorTranslation } from '@frontend/utils';

/*
 * The number input has to be memoized so that it works properly with auto saving. The number input works differently
 * on change (see https://ksyos-it.atlassian.net/wiki/spaces/IT/pages/3229319169/Auto-save), which means that we have
 * to prevent any unnecessary re-renders. A custom comparison function is used, because only the value is relevant
 * for now. If more properties are relevant for re-rendering, include them in the comparison function and be sure to
 * double-check the documentation https://react.dev/reference/react/memo#specifying-a-custom-comparison-function
 */
const MemoizedQuantityNumberInput = memo(
    NumberInput,
    (prevProps, nextProps) =>
        prevProps.value === nextProps.value &&
        prevProps.error === nextProps.error &&
        prevProps.onChange === nextProps.onChange
);

export type QuantityProps = Readonly<{
    isClearable?: boolean;
    maxValue?: number;
    minValue?: number;
    onChange: (quantity: Quantity, changedKey: keyof Quantity) => void;
    quantity: Quantity;
    unitError?: string;
    units: string[];
    valueError?: string;
}>;

export function Quantity({
    isClearable = false,
    maxValue,
    minValue,
    onChange,
    quantity,
    unitError,
    units,
    valueError,
}: QuantityProps): JSX.Element {
    const selectOptions = units.map((unit) => ({
        label: unit,
        value: unit,
    }));

    /*
     * This callback is necessary to correctly memoize the Number Input component. Without this callback,
     * the `onChange` function is a different reference, which causes an unnecessary rerender, which causes
     * the value to be overwritten by auto-save.
     */
    const numberInputOnChange = useCallback(
        (value: number | null): void => {
            onChange(
                {
                    unit: quantity.unit,
                    value: clampValue({ maxValue, minValue, value }),
                },
                'value'
            );
        },
        [maxValue, minValue, quantity.unit, quantity.value]
    );

    return (
        <Row columnGap={3}>
            <Col span={2}>
                <MemoizedQuantityNumberInput
                    error={getValidationErrorTranslation(valueError)}
                    label="value"
                    maximumFractionDigits={2}
                    onChange={numberInputOnChange}
                    value={quantity.value ?? null}
                />
            </Col>
            <Col span={3}>
                <Select
                    error={getValidationErrorTranslation(unitError)}
                    isClearable={isClearable}
                    onChange={(option) =>
                        onChange(
                            {
                                unit: option?.label ?? null,
                                value: quantity.value,
                            },
                            'unit'
                        )
                    }
                    options={selectOptions}
                    placeholder=""
                    value={selectOptions.find((option) => option.value === quantity.unit) ?? null}
                />
            </Col>
        </Row>
    );
}

function clampValue({
    maxValue = Infinity,
    minValue = -Infinity,
    value,
}: {
    maxValue?: number;
    minValue?: number;
    value: number | null;
}): number | null {
    if (value !== null && isFinite(value)) {
        return clamp(value, minValue, maxValue);
    }

    return null;
}
